import { noteBlockForPitch } from './melodiaBuilderConfig';
import { melodyPitchFromSing, type ClingMelodyExercise, type SingPitchId } from './clingCantaConfig';
import { midiNearPitch } from './juniorPitchDetector';
import { lerp, pitchLaneCenterY, clingRollContentHeight, CLING_ROLL_PADDING, midiToLaneY } from './clingCantaPitch';

export type ClingGamePhase = 'idle' | 'demo' | 'countdown' | 'play' | 'finished';

export interface ScrollNote {
  id: string;
  pitchId: SingPitchId;
  x: number;
  width: number;
  hit: boolean;
  missed: boolean;
  fill: string;
  color: string;
  label: string;
}

export interface ClingGameState {
  phase: ClingGamePhase;
  exercise: ClingMelodyExercise;
  clingY: number;
  clingTargetY: number;
  scrollX: number;
  notes: ScrollNote[];
  elapsedMs: number;
  score: number;
  combo: number;
  maxCombo: number;
  hits: number;
  misses: number;
  countdownBeat: number;
  playedDemoNotes: Set<number>;
  feedback: 'hit' | 'miss' | null;
  feedbackUntil: number;
}

export const CLING_X_RATIO = 0.22;
export const HIT_WINDOW_PX = 28;
export const CLING_LERP = 0.28;

let noteCounter = 0;

function beatMs(bpm: number): number {
  return 60000 / bpm;
}

function pixelsPerBeat(bpm: number): number {
  return 88;
}

function noteWidthPx(durationBeats: number, bpm: number): number {
  return durationBeats * pixelsPerBeat(bpm) - 6;
}

function scrollSpeedPxPerMs(bpm: number): number {
  return pixelsPerBeat(bpm) / beatMs(bpm);
}

export function noteVisual(pitchId: SingPitchId) {
  const melodyPitch = melodyPitchFromSing(pitchId);
  if (melodyPitch) {
    const block = noteBlockForPitch(melodyPitch);
    return { fill: block.fill, color: block.color, label: block.label };
  }
  return { fill: '#64748B', color: '#475569', label: pitchId };
}

export function createClingGameState(exercise: ClingMelodyExercise): ClingGameState {
  const midLane = exercise.lanes[Math.floor(exercise.lanes.length / 2)] ?? 'Do4';
  return {
    phase: 'idle',
    exercise,
    clingY: 0,
    clingTargetY: 0,
    scrollX: 0,
    notes: [],
    elapsedMs: 0,
    score: 0,
    combo: 0,
    maxCombo: 0,
    hits: 0,
    misses: 0,
    countdownBeat: 3,
    playedDemoNotes: new Set(),
    feedback: null,
    feedbackUntil: 0,
  };
}

export function spawnNotesForExercise(
  exercise: ClingMelodyExercise,
  stageWidth: number
): ScrollNote[] {
  const ppb = pixelsPerBeat(exercise.bpm);
  return exercise.notes.map((n, i) => {
    const vis = noteVisual(n.pitchId);
    return {
      id: `n-${noteCounter++}`,
      pitchId: n.pitchId,
      x: stageWidth + n.beat * ppb + 40,
      width: noteWidthPx(n.durationBeats, exercise.bpm),
      hit: false,
      missed: false,
      ...vis,
    };
  });
}

export function beginDemo(state: ClingGameState, stageWidth: number): ClingGameState {
  const mid = state.exercise.lanes[Math.floor(state.exercise.lanes.length / 2)]!;
  const clingY = pitchLaneCenterY(mid, state.exercise.lanes);
  return {
    ...state,
    phase: 'demo',
    elapsedMs: 0,
    clingY,
    clingTargetY: clingY,
    notes: spawnNotesForExercise(state.exercise, stageWidth),
    playedDemoNotes: new Set(),
    score: 0,
    combo: 0,
    maxCombo: 0,
    hits: 0,
    misses: 0,
    feedback: null,
    feedbackUntil: 0,
  };
}

export function beginCountdown(state: ClingGameState): ClingGameState {
  return {
    ...state,
    phase: 'countdown',
    elapsedMs: 0,
    countdownBeat: 3,
  };
}

export function beginPlay(state: ClingGameState, stageWidth: number): ClingGameState {
  const mid = state.exercise.lanes[Math.floor(state.exercise.lanes.length / 2)]!;
  const clingY = pitchLaneCenterY(mid, state.exercise.lanes);
  return {
    ...state,
    phase: 'play',
    elapsedMs: 0,
    clingY,
    clingTargetY: clingY,
    notes: spawnNotesForExercise(state.exercise, stageWidth),
    playedDemoNotes: new Set(),
    score: 0,
    combo: 0,
    hits: 0,
    misses: 0,
    feedback: null,
    feedbackUntil: 0,
  };
}

export interface ClingTickResult {
  state: ClingGameState;
  demoNoteIndex: number | null;
  finished: boolean;
}

export function tickClingGame(
  state: ClingGameState,
  dtMs: number,
  stageWidth: number,
  clingX: number,
  voiceMidi: number | null,
  voicePitch: SingPitchId | null,
  isSinging: boolean,
  _stageHeight: number,
  now: number
): ClingTickResult {
  let demoNoteIndex: number | null = null;
  const speed = scrollSpeedPxPerMs(state.exercise.bpm);
  const lanes = state.exercise.lanes;

  if (state.phase === 'countdown') {
    const beat = beatMs(state.exercise.bpm);
    const remaining = 3 - Math.floor(state.elapsedMs / beat);
    const nextElapsed = state.elapsedMs + dtMs;
    const finished = nextElapsed >= 3 * beat + 200;
    return {
      state: {
        ...state,
        elapsedMs: nextElapsed,
        countdownBeat: Math.max(0, remaining),
      },
      demoNoteIndex: null,
      finished,
    };
  }

  if (state.phase !== 'demo' && state.phase !== 'play') {
    return { state, demoNoteIndex: null, finished: false };
  }

  const followVoice = state.phase === 'play';
  const demoPitch = !followVoice
    ? state.exercise.notes.find((n, i) => {
        const tBeat = state.elapsedMs / beatMs(state.exercise.bpm);
        return tBeat >= n.beat && tBeat < n.beat + n.durationBeats && !state.playedDemoNotes.has(i);
      })
    : null;

  if (demoPitch) {
    const idx = state.exercise.notes.indexOf(demoPitch);
    if (idx >= 0 && !state.playedDemoNotes.has(idx)) {
      demoNoteIndex = idx;
    }
  }

  const contentH = clingRollContentHeight(lanes.length);
  const fallY = contentH - CLING_ROLL_PADDING;

  let clingTargetY = state.clingTargetY;
  if (followVoice && isSinging) {
    if (voiceMidi != null) {
      clingTargetY = midiToLaneY(voiceMidi, lanes);
    } else if (voicePitch) {
      clingTargetY = pitchLaneCenterY(voicePitch, lanes);
    }
  } else if (!followVoice && demoPitch) {
    clingTargetY = pitchLaneCenterY(demoPitch.pitchId, lanes);
  } else if (!followVoice) {
    const neutral = lanes[Math.floor(lanes.length / 2)]!;
    clingTargetY = pitchLaneCenterY(neutral, lanes);
  } else if (followVoice && !isSinging) {
    clingTargetY = lerp(state.clingTargetY, fallY, 0.06);
  }

  const clingY = lerp(state.clingY, clingTargetY, CLING_LERP);

  let score = state.score;
  let combo = state.combo;
  let maxCombo = state.maxCombo;
  let hits = state.hits;
  let misses = state.misses;
  let feedback = state.feedback;
  let feedbackUntil = state.feedbackUntil;

  if (now >= feedbackUntil) {
    feedback = null;
  }

  const notes = state.notes.map((note) => ({
    ...note,
    x: note.x - speed * dtMs,
  }));

  const scrollX = state.scrollX + speed * dtMs;

  if (state.phase === 'play') {
    for (const note of notes) {
      if (note.hit || note.missed) continue;
      const inWindow = Math.abs(note.x + note.width / 2 - clingX) <= HIT_WINDOW_PX;
      const passed = note.x + note.width < clingX - HIT_WINDOW_PX;

      if (inWindow && isSinging) {
        const pitchMatch =
          voiceMidi != null
            ? midiNearPitch(voiceMidi, note.pitchId)
            : voicePitch === note.pitchId;
        if (pitchMatch) {
          note.hit = true;
          hits += 1;
          combo += 1;
          maxCombo = Math.max(maxCombo, combo);
          score += 100 + combo * 10;
          feedback = 'hit';
          feedbackUntil = now + 280;
        }
      } else if (passed) {
        note.missed = true;
        misses += 1;
        combo = 0;
        feedback = 'miss';
        feedbackUntil = now + 280;
      }
    }
  }

  const elapsedMs = state.elapsedMs + dtMs;
  const totalMs =
    state.exercise.notes.reduce((m, n) => Math.max(m, n.beat + n.durationBeats), 0) *
      beatMs(state.exercise.bpm) +
    1400;
  const allDone = notes.every((n) => n.hit || n.missed || n.x + n.width < -20);
  const finished =
    state.phase === 'demo'
      ? elapsedMs >= totalMs
      : allDone || elapsedMs >= totalMs + 800;

  let playedDemoNotes = state.playedDemoNotes;
  if (demoNoteIndex != null) {
    playedDemoNotes = new Set(state.playedDemoNotes);
    playedDemoNotes.add(demoNoteIndex);
  }

  return {
    state: {
      ...state,
      elapsedMs,
      scrollX,
      clingY,
      clingTargetY,
      notes,
      score,
      combo,
      maxCombo,
      hits,
      misses,
      feedback,
      feedbackUntil,
      playedDemoNotes,
      phase: finished && state.phase === 'play' ? 'finished' : state.phase,
    },
    demoNoteIndex,
    finished: state.phase === 'demo' && finished,
  };
}

export function clingHitbox(): { width: number; height: number } {
  return { width: 48, height: 28 };
}
